import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { ReasonDefinition } from '@modules/taxonomy/store/types';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { Box, Divider, List, Stack, Typography } from '@mui/material';
import { EmptyReasonDefinitionContainer } from './EmptyReasonDefinitionContainer';
import { ReasonDefinitionListItem } from './ReasonDefinitionListItem';
import {
  getSelectedUnassignedDefinitions,
  trackCreateNewReasonEvents,
} from './helpers';

export const SelectedReasonDefinitionList = () => {
  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);
  const reasonDefinitions = useTaxonomyStore(
    ({ createReasonV3State: { reasonDefinitions } }) => reasonDefinitions,
  );

  const generatedReasonDefinitions = reasonDefinitions?.filter(
    (definition) => definition.__typename === 'GeneratedRC',
  );
  const selectedUnassignedDefinitions =
    getSelectedUnassignedDefinitions(reasonDefinitions);

  const reasonDefinitionsToDisplay = [
    ...(generatedReasonDefinitions ?? []),
    ...(selectedUnassignedDefinitions ?? []),
  ];

  const onRemoveDefinition = (definition: ReasonDefinition) => {
    if (
      definition.__typename === 'GeneratedRC' ||
      definition.__typename === 'SearchedUnassignedRc'
    ) {
      const updatedReasonDefinitions = reasonDefinitions?.filter(
        (reasonDefinition) => reasonDefinition.name !== definition.name,
      );

      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          reasonDefinitions: updatedReasonDefinitions,
        },
      });
      trackCreateNewReasonEvents({
        eventName: 'Create New Reason - Selected Reason Definition Removed',
        reasonDefinition: {
          type: definition.__typename,
          name: definition.name,
          category: mapEdgeCategoryToCategory(definition.category),
        },
      });
      return;
    }

    const updatedReasonDefinitions = reasonDefinitions?.map(
      (reasonDefinition) => {
        if (reasonDefinition.name === definition.name) {
          return {
            ...definition,
            isSelected: false,
          };
        }
        return reasonDefinition;
      },
    );

    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        reasonDefinitions: updatedReasonDefinitions,
      },
    });
    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Selected Reason Definition Removed',
      reasonDefinition: {
        type: 'UnassignedRC',
        name: definition.name,
        category: mapEdgeCategoryToCategory(definition.category),
      },
    });
  };

  return (
    <>
      <Stack spacing={1.25}>
        <Stack px={1.25} pt={0.25}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleOutlineRoundedIcon color="success" />
            <Typography component="span" variant="subtitle1">
              Selected Definitions
            </Typography>
            <Typography
              color="text.secondary"
              component="span"
              variant="caption"
            >
              (These Definitions will be used to find relevant feedback)
            </Typography>
          </Stack>
        </Stack>
        <Divider sx={{ opacity: 0.12 }} />
      </Stack>
      <Box height="100%" overflow="auto">
        {!reasonDefinitionsToDisplay.length ? (
          <EmptyReasonDefinitionContainer
            icon={<>➡️</>}
            primaryText="Looks like there are no definitions added yet."
            secondaryText="Add definitions from the right to enrich this list!"
          />
        ) : (
          <List disablePadding style={{ marginTop: 0, padding: 0 }}>
            {reasonDefinitionsToDisplay.map((definition) => {
              return (
                <ReasonDefinitionListItem
                  key={definition.name}
                  definition={definition}
                  onRemoveDefinition={() => onRemoveDefinition(definition)}
                />
              );
            })}
          </List>
        )}
      </Box>
    </>
  );
};
